import { energySystemDemandResponseEventsCollection } from '@/reducers/energySystemDemandResponseEvents'
import { EnergySystemDemandResponseEvent } from '@/types/energySystemDemandResponseEvent'
import { P, match } from 'ts-pattern'
import EnergySystemUpcomingDRAlert from './EnergySystemUpcomingDRAlert'
import { energySystemCollection } from '@/reducers/energySystems'
import EnergySystemActiveDRAlert from './EnergySystemActiveDRAlert'

type Props = {
  energySystemId: string
}

const LOOK_AHEAD_HOURS = 24

function getDREventAlertMode(event: EnergySystemDemandResponseEvent) {
  const now = new Date().getTime()
  const mode = match({
    start_inclusive: new Date(event.start_inclusive).getTime(),
    end_exclusive: new Date(event.end_exclusive).getTime(),
  })
    .with(
      { start_inclusive: P.number.gt(now), end_exclusive: P.number.gt(now) },
      () => 'upcoming' as const,
    )
    .with(
      { start_inclusive: P.number.lt(now), end_exclusive: P.number.lt(now) },
      () => 'past' as const,
    )
    .with(
      { start_inclusive: P.number.lt(now), end_exclusive: P.number.gt(now) },
      () => 'active' as const,
    )
    .otherwise(() => 'error' as const)

  return mode
}

export function useViewModel(props: Props) {
  const { data: energySystems, isLoadingOrIdle: isLoadingEnergySystems } =
    energySystemCollection.useFetch('energy-systems')
  const energySystem = energySystems?.find(
    (energySystem) => energySystem.id === props.energySystemId,
  )
  const { data: energySystemDemandResponseEvents } =
    energySystemDemandResponseEventsCollection.useFetch(
      `energy-systems/${props.energySystemId}/dr-events`,
      {
        cacheKey: 'upcoming-energy-system-dr-events',
        params: {
          start_inclusive: new Date().toISOString(),
          // look ahead 24 hours
          end_exclusive: new Date(
            Date.now() + LOOK_AHEAD_HOURS * 60 * 60 * 1000,
          ).toISOString(),
        },
      },
    )

  const upcomingEvents = energySystemDemandResponseEvents?.filter(
    (event) => getDREventAlertMode(event) === 'upcoming',
  )
  const activeEvents = energySystemDemandResponseEvents?.filter(
    (event) => getDREventAlertMode(event) === 'active',
  )

  return { upcomingEvents, activeEvents, energySystem, isLoadingEnergySystems }
}

export default function EnergySystemDRAlert(props: Props) {
  const { upcomingEvents, activeEvents, energySystem, isLoadingEnergySystems } =
    useViewModel(props)

  if (!energySystem || isLoadingEnergySystems) {
    return null
  }

  return (
    <>
      {activeEvents?.map((event) => (
        <EnergySystemActiveDRAlert
          key={event.id}
          event={event}
          energySystem={energySystem}
        />
      ))}
      {upcomingEvents?.map((event) => (
        <EnergySystemUpcomingDRAlert
          key={event.id}
          event={event}
          energySystem={energySystem}
        />
      ))}
    </>
  )
}
